import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { FunnelIcon } from '@heroicons/react/20/solid';
import { PropertyPicker, chipClass } from './PropertyPicker';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import {
  SEVERITIES,
  STATUSES,
  VIOLATION_TYPES,
  ViolationFilters,
  ViolationStatus,
  statusLabel,
} from './violationMeta';

/**
 * Linear's filter bar (#603 Part 2 §2): one chip per active filter, each
 * reading as a sentence — "Status is any of Pending, In Progress" — with
 * an × to remove it, and unset filters offered as quiet "+ Status"
 * buttons. Filters combine with AND ("match all filters").
 */
interface Props {
  filters: ViolationFilters;
  onChange: (next: ViolationFilters) => void;
  shown: number;
  total: number;
}

const summarise = (values: string[], label: (v: string) => string) =>
  values.length <= 2
    ? values.map(label).join(', ')
    : `${values.length} selected`;

/**
 * One filter, active or not. The picker sits in the same child slot either
 * way (the conditional siblings render as `false`, which keeps positions),
 * so choosing a value — which turns "+ Status" into "Status is Pending" —
 * does not remount the open popover and a multi-select stays open.
 */
const FilterField: React.FC<{
  field: string;
  values: string[];
  label: (v: string) => string;
  onClear: () => void;
  picker: (trigger: React.ReactNode) => React.ReactNode;
}> = ({ field, values, label, onClear, picker }) => {
  const active = values.length > 0;
  return (
    <span
      className={clsx(
        'inline-flex items-center text-[13px]',
        active && 'rounded-md border border-line'
      )}
    >
      {active && (
        <span className="px-2 py-1 text-fg-muted">{field}</span>
      )}
      {active && (
        <span className="border-l border-line px-1 text-fg-muted">
          {values.length > 1 ? 'is any of' : 'is'}
        </span>
      )}
      <span className={clsx(active && 'border-l border-line')}>
        {picker(
          active ? (
            <span className="font-medium">{summarise(values, label)}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-fg-muted">
              <span aria-hidden>+</span> {field}
            </span>
          )
        )}
      </span>
      {active && (
        <button
          type="button"
          onClick={onClear}
          aria-label={`Remove ${field} filter`}
          className="border-l border-line px-1.5 py-1 text-fg-muted hover:text-fg"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
};

export const ViolationFilterBar: React.FC<Props> = ({
  filters,
  onChange,
  shown,
  total,
}) => {
  const statusOptions = STATUSES.map((s) => ({
    value: s.value,
    label: s.label,
    icon: <StatusIcon status={s.value} />,
  }));
  const severityOptions = SEVERITIES.map((s) => ({
    value: s,
    label: s,
    icon: <SeverityGlyph severity={s} />,
  }));
  const typeOptions = VIOLATION_TYPES.map((t) => ({ value: t, label: t }));

  const statusPicker = (trigger: React.ReactNode) => (
    <PropertyPicker
      multiple
      options={statusOptions}
      value={filters.statuses}
      onChange={(v) => onChange({ ...filters, statuses: v as ViolationStatus[] })}
      placeholder="Status…"
      ariaLabel="Filter by status"
      trigger={trigger}
    />
  );
  const severityPicker = (trigger: React.ReactNode) => (
    <PropertyPicker
      multiple
      options={severityOptions}
      value={filters.severities}
      onChange={(v) => onChange({ ...filters, severities: v })}
      placeholder="Severity…"
      ariaLabel="Filter by severity"
      trigger={trigger}
    />
  );
  const typePicker = (trigger: React.ReactNode) => (
    <PropertyPicker
      multiple
      options={typeOptions}
      value={filters.types}
      onChange={(v) => onChange({ ...filters, types: v })}
      placeholder="Type…"
      ariaLabel="Filter by type"
      trigger={trigger}
    />
  );

  const anyActive =
    filters.statuses.length || filters.severities.length || filters.types.length;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line/60 px-3 py-2">
      <FunnelIcon className="h-4 w-4 text-fg-faint" aria-hidden />

      <FilterField
        field="Status"
        values={filters.statuses}
        label={statusLabel}
        onClear={() => onChange({ ...filters, statuses: [] })}
        picker={statusPicker}
      />
      <FilterField
        field="Severity"
        values={filters.severities}
        label={(v) => v}
        onClear={() => onChange({ ...filters, severities: [] })}
        picker={severityPicker}
      />
      <FilterField
        field="Type"
        values={filters.types}
        label={(v) => v}
        onClear={() => onChange({ ...filters, types: [] })}
        picker={typePicker}
      />

      {anyActive ? (
        <button
          type="button"
          className={clsx(chipClass, 'text-fg-muted')}
          onClick={() =>
            onChange({ ...filters, statuses: [], severities: [], types: [] })
          }
        >
          Clear
        </button>
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        <span className="text-[12px] text-fg-muted" aria-live="polite">
          {shown === total ? `${total}` : `${shown} of ${total}`}
        </span>
        <label className="flex items-center gap-1.5 rounded-md border border-line px-2 py-1 focus-within:ring-2 focus-within:ring-blue-500">
          <MagnifyingGlassIcon className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            placeholder="Search"
            aria-label="Search violations"
            className="w-40 border-0 bg-transparent p-0 text-[13px] text-fg placeholder:text-zinc-500 focus:outline-none focus:ring-0"
          />
        </label>
      </div>
    </div>
  );
};
